import { KTestConfig, KTestFunction, YupSchema } from 'services/yup/types'
import {
  yupArray,
  yupArrayWithContext,
  yupIgnore,
  yupNumber,
  yupObject,
  yupString,
} from 'services/yup'
import {
  areRulesNotOverlappingCFG,
  byRulePricingCommonVS,
  withCostAndPrice,
} from './by-rule-pricing/byRulePricing'
import { withUpsellingProductVS } from './withUpsellingProduct'
import { TimeLabel } from 'services/utils/date'
import {
  PricingTimePeriodRule,
  PricingMinHoursRule,
  RulePricingType,
} from 'services/api/entities/catalog/types'
import { LocationArrangementStatus, ActivityStatus } from 'types/form'
import { isNameUniqueCFG } from '../common'
import { TestConfig, TestFunction } from 'yup'
import { isTimeLabelValid, areSetsEqual } from 'services/utils'
import { getSetFromHalfSteps } from 'services/utils/validation'
import { locationPricingNameChoices } from 'services/cfg/form'
import {
  LocationPricingRuleSet,
  LocationArrangementConfig,
  LocationArrangementMap,
  Location,
} from 'services/api/entities/catalog/location/types/internal/location-models'

// !Tets Fns
const validateHasFullDay: TestFunction<LocationPricingRuleSet[]> = (pricing) =>
  !!pricing?.every((p) =>
    p?.pricingRules?.some((r) => r?.name === locationPricingNameChoices[3].id)
  )

const validateNotAllExtraRules: KTestFunction<
  (PricingTimePeriodRule | PricingMinHoursRule)[]
> = (rules) => !rules?.every((r) => r?.isExtra)

const validateHasMinimumNHR: KTestFunction<
  (PricingTimePeriodRule | PricingMinHoursRule)[]
> = (rules) =>
  !!rules?.some((r) => r?.mode === 'nhr' && r?.atLeast === 0.5 && !r?.isExtra)

const validateAtLeastGreaterThanMinHours: KTestFunction<
  LocationPricingRuleSet[]
> = (ruleSet) =>
  !!ruleSet?.every((p) => {
    // If pricing.minHours is not compiled return true to avoid validation
    if (!p?.minHours) return true
    const nhrs = p.pricingRules?.filter((r) => r?.mode === 'nhr') as
      | Partial<PricingMinHoursRule | undefined>[]
      | undefined
    const nonDefaultNhrs = nhrs
      // Filtering out the default NHR (which is set atLeast=0 and isExtra=false) and the ones with atLeast=undefined
      ?.filter((r) => !(r?.atLeast === 0.5 && !r?.isExtra))
      // Filtering out the NHR that haven't been compiled yet
      .filter((r) => r?.atLeast)

    return nonDefaultNhrs?.every((nhr) => nhr?.atLeast! >= p.minHours!)
  })

const validateTPRCoverage: KTestFunction<
  (PricingTimePeriodRule | PricingMinHoursRule)[],
  Location
> = (rules, { parent }): boolean => {
  // This validation will only be applied when every rules is a TPR
  // @see https://gitlab.com/kampaay/general/-/issues/936#note_1016582293
  if (!rules?.every((r) => r?.mode === 'tpr')) return true

  const tpr = (rules ?? []).filter(
    (r) =>
      (r as PricingTimePeriodRule)?.hoursFrom &&
      (r as PricingTimePeriodRule)?.hoursTo &&
      !r.isExtra
  ) as PricingTimePeriodRule[]

  // User hasn't finished to specify availableFrom, availableTo or doesn't have any time period rules
  if (
    !tpr.length ||
    !isTimeLabelValid(parent!.availableFrom) ||
    !isTimeLabelValid(parent!.availableTo)
  )
    return true

  const timeRangeNeeded = getSetFromHalfSteps({
    start: parent!.availableFrom!,
    end: parent!.availableTo!,
  })

  const timeRangeCoveredByRules = getSetFromHalfSteps(
    ...tpr.map((r) => ({ start: r.hoursFrom, end: r.hoursTo }))
  )

  return !!areSetsEqual(timeRangeNeeded, timeRangeCoveredByRules)
}

// !Configs
const isAtLeastOneRuleFullDayCFG: TestConfig<LocationPricingRuleSet[]> = {
  test: validateHasFullDay,
  message: 'form.validations.atLeastOneRuleWithFullDayName',
}

const hasMinimumNHR: TestConfig<
  (PricingTimePeriodRule | PricingMinHoursRule)[]
> = {
  test: validateHasMinimumNHR,
  message: 'form.validations.minimumNHRRequired',
}

const isAllExtraRuleCFG: KTestConfig<
  (PricingTimePeriodRule | PricingMinHoursRule)[]
> = {
  test: validateNotAllExtraRules,
  message: 'form.validations.notAllExtraRules',
}

const isAtLeastOneNHRGreaterThanMinHoursCFG: KTestConfig<
  LocationPricingRuleSet[]
> = {
  test: validateAtLeastGreaterThanMinHours,
  message: 'form.validations.atLeastOneNHRGreaterThanMinHours',
}

const areAllTPRCoveringTheAvailableTimePeriodCFG: KTestConfig<
  (PricingTimePeriodRule | PricingMinHoursRule)[],
  Location
> = {
  test: validateTPRCoverage,
  message: 'form.validations.areAllTPRCoveringTheAvailableTimePeriod',
}

// !Schemas
const pricingRuleVS: YupSchema<PricingTimePeriodRule | PricingMinHoursRule> =
  withCostAndPrice.shape({
    mode: yupIgnore().required(),
    name: yupString(),
    priceType: yupString<RulePricingType>(),
    isExtra: yupIgnore(),
    atLeast: yupNumber<PricingMinHoursRule>().when('mode', {
      is: 'nhr',
      then: (schema) => schema.min(0.5).required(),
    }),
    hoursFrom: yupString<TimeLabel>().when('mode', {
      is: 'tpr',
      then: (schema) => schema.required(),
    }),
    hoursTo: yupString<TimeLabel>().when('mode', {
      is: 'tpr',
      then: (schema) => schema.required(),
    }),
  })

const locationByRulePricingArrayVS: YupSchema<LocationPricingRuleSet[]> =
  yupArray(
    byRulePricingCommonVS.shape({
      pricingRules: yupArrayWithContext<Location>()(pricingRuleVS)
        .min(1)
        .test(isNameUniqueCFG)
        .test(isAllExtraRuleCFG)
        .test(areAllTPRCoveringTheAvailableTimePeriodCFG)
        .test(hasMinimumNHR),
    })
  )
    .test(isAtLeastOneRuleFullDayCFG)
    .test(isAtLeastOneNHRGreaterThanMinHoursCFG)
    .test(areRulesNotOverlappingCFG)
    .min(1)

const locationArrangementCFGVS: YupSchema<LocationArrangementConfig> =
  yupObject().shape({
    capacity: yupNumber(),
    status: yupString<LocationArrangementStatus>().required(),
  })

const locationArrangementMapVS: YupSchema<LocationArrangementMap> =
  yupObject().shape({
    classroom: locationArrangementCFGVS,
    theater: locationArrangementCFGVS,
    cabaret: locationArrangementCFGVS,
    boardroom: locationArrangementCFGVS,
    ushape: locationArrangementCFGVS,
    standing: locationArrangementCFGVS,
  })

export const locationVS: YupSchema<Location> = withUpsellingProductVS.shape({
  nickname: yupString().required(),
  status: yupString<ActivityStatus>().required(),
  fullAddress: yupString().required(),
  peopleCapacity: yupNumber().required().min(0),
  supplier: yupNumber().required(),
  roomCapacity: yupNumber().required().min(0),
  defaultHoursBefore: yupNumber().required().min(0),
  defaultHoursAfter: yupNumber().required().min(0),
  availableFrom: yupString<TimeLabel>().required(),
  availableTo: yupString<TimeLabel>().required(),
  pricing: locationByRulePricingArrayVS,
  planimetry: yupIgnore(),
  locationZone: yupIgnore(),
  supplierName: yupIgnore(),
  equipments: yupArray(),
  characteristics: yupArray(),
  filterLocationFacet: yupIgnore(),
  tagFacets: yupIgnore(),
  arrangements: locationArrangementMapVS,
  eventTypeFacet: yupIgnore(),
})
